import { Fragment, FC, useEffect, useState } from "react";
import { Box, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { CommonStyle, Loader } from "Components";
import { SideModalProps } from "DataTypes/Modal";
import TabPanel from "Components/TabPanel";
import ImgPath from "Constant/Imgs";
import RedoxApi from "Service/Redox.api";
import RedoxFormatter from "Utils/Redox.Formatter";
import { MedSearchType } from "DataTypes/Services/Redox.type";
import { PatientInitialStateType, UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { label } from "Constant/En";
import DateTime from "Utils/DateTime";
import NoData from "Components/NoData";
import AddEditMeds from "./AddEditMeds";
import CstmBtn from "Components/CstmBtn";
import useEhrSync from "Hooks/useEhrSync";
import EhrLastSync from "Components/EhrLastSync";
import useEhrRefresh from "Hooks/useEhrRefresh";
import useClinicalCounts from "Hooks/useClinicalCounts";
import SortBar, { SortStateType } from "Components/SortBar";
import { EhrDomains } from "Constant/Ehr";
import { sortOptions } from "./Data";

const a11yProps: (index: number) => {
  id: string;
  "aria-controls": string;
} = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export interface DataActionType {
  data: MedSearchType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const initailState: DataActionType = { data: null, action: null };

const initialSort: SortStateType = { sortBy: "name", sortOrder: "asc" };

const Meds: FC<SideModalProps> = ({ onClose }) => {
  const { patientInfo, patientOrg, ehr }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient,
  );
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user,
  );
  const crudPermission = myInfo.roleId != 6 && ehr.vendor === null;
  const [medsList, setMedsList] = useState<MedSearchType[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [sort, setSort] = useState<SortStateType>(initialSort);
  const [dataAction, setDataAction] = useState<DataActionType>(initailState);

  useEffect(() => {
    getMedsData();
  }, [tabValue, sort]);

  // Only a tab change closes the add / edit panel; re-sorting leaves it open.
  useEffect(() => {
    setDataAction(initailState);
  }, [tabValue]);

  const getMedsData: () => void = async () => {
    setLoading(true);
    const payload = {
      medicationsActive: tabValue == 0 ? "active" : "history",
      sortBy: sort.sortBy,
      sortOrder: sort.sortOrder,
    };
    const res: any = await RedoxApi.MedSearch({
      id1: patientInfo.id,
      data: payload,
    });
    if (res?.success) {
      const formatedData = RedoxFormatter.MedSearch(res?.data?.medications);
      setMedsList(formatedData);
    } else setMedsList([]);
    setLoading(false);
  };

  const ehrSync = useEhrSync(EhrDomains.MEDICATIONS);
  useEhrRefresh(EhrDomains.MEDICATIONS, getMedsData);

  const { counts, refreshCounts } = useClinicalCounts();
  // Undefined until the counts load, so no made-up zero is shown.
  const activeCount: number | undefined = counts?.medications?.active;
  const historicalCount: number | undefined = counts?.medications?.historical;
  // An add, edit or delete moves the tab counts as well as the list.
  const refreshListAndCounts: () => void = () => {
    getMedsData();
    refreshCounts();
  };

  const handleCloseLayer2: () => void = () => {
    setDataAction(initailState);
  };

  const displayMedsHead: (data: MedSearchType) => React.ReactElement = (
    data,
  ) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.productName || label.naText}
        </Typography>
        {crudPermission && (
          <Box display="flex" sx={{ "&>div": { ml: 1 } }} width="15%">
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "EDIT" })}
            >
              <ImgPath.EditOutlined />
            </Box>
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => setDataAction({ data: data, action: "DELETE" })}
            >
              <ImgPath.DeleteOutline />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const HistoricalUIContent = (
    <Box sx={{ ...sxMain }}>
      {loading ? (
        <Loader />
      ) : medsList.length ? (
        medsList.map((el) => {
          return (
            <Box key={el.id} sx={{ ...sxContainer }}>
              {displayMedsHead(el)}
              <Typography variant="subtitle1">
                Stop Date: {DateTime.ToLocalDate(el.endDate) || label.naText}
              </Typography>
            </Box>
          );
        })
      ) : (
        <NoData />
      )}
    </Box>
  );

  const ActiveUIContent = (
    <Box sx={{ ...sxMain }}>
      {loading ? (
        <Loader />
      ) : medsList.length ? (
        medsList.map((el) => {
          return (
            <Box key={el.id} sx={{ ...sxContainer }}>
              {displayMedsHead(el)}
              <Box display="flex" justifyContent="space-between">
                <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                  Route: {el.routeName || label.naText}
                </Typography>
                <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                  Dose:{" "}
                  {`${
                    (el.doseQuantity + " " + el.doseUnits).trim() ||
                    label.naText
                  }`}
                </Typography>
                <Typography variant={"subtitle1"} sx={{ ...sxMedDetails }}>
                  Frequency:{" "}
                  {`${
                    (el.frequencyPeriod + " " + el.frequencyUnit).trim() ||
                    label.naText
                  }`}
                </Typography>
              </Box>
              {/* The sig is the prescriber's instruction text. eCW sends it as
                  dosageInstruction[0].text and it is the only note-like field
                  on a MedicationRequest, so show it when present. */}
              {el.freeTextSig && (
                <Typography variant={"subtitle1"} sx={{ ...sxMedNote }}>
                  Note: {el.freeTextSig}
                </Typography>
              )}
            </Box>
          );
        })
      ) : (
        <NoData />
      )}
    </Box>
  );

  const Layer1 = (
    <Fragment>
      <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
        <Typography fontWeight="bold">Medications</Typography>
        {/* Controls on one line, with the sync stamp beneath them:
            stacking it under the button alone makes that column taller
            and drops the icons out of line with it. */}
        <Box
          sx={{
            ...CommonStyle.sxFlexDirectionColumn,
            alignItems: "flex-end",
          }}
        >
          <Box sx={{ ...CommonStyle.sxRow }}>
            {ehrSync.canSync && (
              <Tooltip arrow placement="top" title={ehrSync.title}>
                <span>
                  <CstmBtn
                    label={label.syncFromEhr}
                    btnType="primary"
                    loading={ehrSync.syncing}
                    loadingText="Syncing..."
                    onClick={ehrSync.handleSync}
                    sxProps={{ mr: 1, py: 0.4, minWidth: "auto", px: 1.2 }}
                  />
                </span>
              </Tooltip>
            )}
            {crudPermission && (
              <Box
                sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
                onClick={() => setDataAction({ data: null, action: "ADD" })}
              >
                <ImgPath.PlusIcon titleAccess="Add Medication" />
              </Box>
            )}
            <Box
              sx={{ ...CommonStyle.sxRoundIconsMainSm }}
              onClick={() => {
                handleCloseLayer2();
                onClose();
              }}
            >
              <ImgPath.CloseIcon titleAccess="Close" />
            </Box>
          </Box>
          <EhrLastSync sync={ehrSync} />
        </Box>
      </Box>
      <Box sx={{ ...sxTab }}>
        <Tabs
          value={tabValue}
          onChange={(event, index) => {
            setTabValue(index);
          }}
          aria-label="basic tabs example"
          variant="fullWidth"
          indicatorColor="secondary"
        >
          <Tab
            label={
              activeCount == null ? "Active" : `Active (${activeCount})`
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              historicalCount == null
                ? "Historical"
                : `Historical (${historicalCount})`
            }
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>
      <SortBar
        options={sortOptions}
        sort={sort}
        onChange={setSort}
      />
      <TabPanel value={tabValue} index={0} style={{ ...sxTabPanel }}>
        {ActiveUIContent}
      </TabPanel>
      <TabPanel value={tabValue} index={1} style={{ ...sxTabPanel }}>
        {HistoricalUIContent}
      </TabPanel>
    </Fragment>
  );

  return (
    <Fragment>
      {/* The list column keeps a pixel floor so its rows stop collapsing
          into a horizontal scroll on a narrow screen, and the panel beside
          it takes whatever width is left. */}
      <Box
        sx={{
          ...CommonStyle.sxFlexDirectionColumn,
          width: "40%",
          minWidth: 320,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {Layer1}
      </Box>
      <Box width="1%"></Box>
      {crudPermission && dataAction.action && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AddEditMeds
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={refreshListAndCounts}
          />
        </Box>
      )}
    </Fragment>
  );
};
export default Meds;

const sxTab = {
  flexShrink: 0,
  px: 2,
  "&>div": {
    borderBottom: 1,
    borderColor: "custom.main",
  },
};

// The header sizes to its own content rather than to a percentage of the
// modal: with the sync stamp on a second line it needs more than the 10% a
// short viewport gives it, and the overflow used to land on the list below.
const sxHeader = {
  justifyContent: "space-between",
  flexShrink: 0,
  py: 1,
  px: 2,
};

const sxTabPanel = {
  flex: 1,
  minHeight: 0,
};

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};

const sxMedDetails = {
  width: "32%",
  lineBreak: "anywhere",
};

const sxMedNote = {
  color: "secondary.main",
  fontStyle: "italic",
  mt: 0.5,
};
